/**
 * manpowerHelpers.js - colour derivation (a stable colour per project code,
 * with a readable foreground) and the date maths the calendar grid needs.
 * All pure.
 */
import { localDay as ymd } from '../../utils/localDay';

export function defaultProjectColor(code) {
  if (!code) return '#92D050';
  let h = 0;
  for (let i = 0; i < code.length; i++) h = (h * 31 + code.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  const sat = 62 + (h % 18);
  const lit = 42 + ((h >> 8) % 10);
  return hslToHex(hue, sat, lit);
}

// HSL → HEX so localStorage / color input stay in sync (color picker
// only accepts hex).
export function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => {
    const v = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  };
  return '#' + f(0) + f(8) + f(4);
}

// Decide a readable foreground (white vs black) based on background luminance.
export function fgFor(hex) {
  if (!hex || !hex.startsWith('#')) return '#fff';
  const m = hex.match(/^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return '#fff';
  const r = parseInt(m[1], 16),
    g = parseInt(m[2], 16),
    b = parseInt(m[3], 16);
  // Relative luminance per WCAG simplified
  const L = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return L > 0.6 ? '#0D3D14' : '#fff';
}

export function avatarInitials(name) {
  return (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export { ymd };

export function shortDate(s) {
  // A YYYY-MM-DD string is a LOCAL day. new Date('2026-09-14') is UTC midnight,
  // which is the evening before in any US timezone - so every week column was
  // labelled with the Sunday before its Monday ("Sep 13" for Sep 14).
  const d =
    typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
      ? (() => {
          const [y, m, dd] = s.split('-').map(Number);
          return new Date(y, m - 1, dd);
        })()
      : new Date(s);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * The Mon-Fri span a week column stands for, as real YYYY-MM-DD dates.
 *
 * A weekly or quarterly column is keyed "2026-W38" and a yearly one "2026-09".
 * Those went straight into the cell editor as if they were dates, so saving an
 * assignment in any view except monthly parsed to Invalid Date and wrote
 * nothing at all - the modal closed and the grid stayed empty. Columns carry
 * an explicit from/to now.
 */
export function weekSpan(monday) {
  const start =
    typeof monday === 'string'
      ? (() => {
          const [y, m, d] = monday.split('-').map(Number);
          return new Date(y, m - 1, d);
        })()
      : new Date(monday);
  const end = new Date(start);
  end.setDate(end.getDate() + 4); // Mon -> Fri; nobody schedules weekends
  // ...but a block drawn in the monthly view can include a Sat/Sun, and the week
  // column shows it. Moving or removing that block has to clear through Sunday
  // or the weekend days keep the old project showing in the week.
  const sun = new Date(start);
  sun.setDate(sun.getDate() + 6);
  return { from: ymd(start), to: ymd(end), spanEnd: ymd(sun) };
}

export function isoWeekKey(d) {
  const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNr = (target.getUTCDay() + 6) % 7;
  target.setUTCDate(target.getUTCDate() - dayNr + 3);
  const firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
  const diff = (target - firstThursday) / 86400000;
  const wk = 1 + Math.round((diff - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${target.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`;
}

export function daysOfMonth(monthRef) {
  const y = monthRef.getFullYear(),
    m = monthRef.getMonth();
  const last = new Date(y, m + 1, 0).getDate();
  const arr = [];
  for (let d = 1; d <= last; d++) arr.push(new Date(y, m, d));
  return arr;
}

// The week axis for the weekly + quarterly views. It used to be a list baked
// into the Manpower Planning export, which is why the calendar could only show
// the weeks that spreadsheet happened to cover. Generated now: every ISO week
// from two years back to three years ahead, so navigation never runs out.
export function buildWeeks(today = new Date()) {
  const first = new Date(today.getFullYear() - 2, 0, 1);
  const last = new Date(today.getFullYear() + 3, 11, 31);
  // Walk back to the Monday of the first week.
  const d = new Date(first);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  const out = [];
  while (d <= last) {
    out.push({ iso: isoWeekKey(d), monday: ymd(d) });
    d.setDate(d.getDate() + 7);
  }
  return out;
}
